import { useAsyncExecutor } from "async-executor-hook";
import { Paginator } from "primereact/paginator";
import { useEffect, useState } from "react";

import { Authorization } from "../../common/AuthorizationCpt";
import { getErrorValueResponse } from "../../common/error/Error";
import { generateSequentialArray } from "../../common/utils";
import { HTTP_METHODS } from "../../utils/constants";
import { dateToRegularFormat } from "../../utils/dates";
import { networkBackendRequest } from "../../utils/networkUtils";
import CommentModal from "../CommentModal/CommentModal";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {
  Container,
  FakeRow,
  Row,
  TableBody,
  TableHeader,
} from "../DocumentManagement/DocumentManagementSharedStyles";
import FileUploadSenasaModal from "../FileUploadSenasaModal/FileUploadSenasaModal";
import StandaloneMessage from "../StandaloneMessage/StandaloneMessage";
import StatusTag from "../StatusTag/StatusTag";
import { Span, Title } from "./AffectedDamsStyles";
import { convertSenasaOrElectronic } from "./ConvertSenasaElectronic";
import SenasaDamsFilter, {
  formatFilters,
  INITIAL_FILTERS,
} from "./SenasaDamsFilter";

const ITEMS_PER_PAGE = 10;

const TITLES = {
  dam: "DAM/CONSIGNATARIO",
  status: "ESTADO",
  date: "FECHA DE ACTUALIZACIÓN DEL DOCUMENTO",
  volantes: "VOLANTES FACTURADOS",
};

const SenasaDams = () => {
  const getSenasaDams = useAsyncExecutor(
    async (params: any) => {
      const paramsFilter = formatFilters(params.filters);
      const paramsSearch = {
        ...paramsFilter,
        order: "descending",
        page_size: ITEMS_PER_PAGE,
        page: params.page ?? 1,
      };
      return await networkBackendRequest(
        "/dams/senasa-agent/",
        HTTP_METHODS.GET,
        paramsSearch
      )
        .then((resp) => resp.json())
        .then(convertSenasaOrElectronic);
    },

    {
      onError: (error) => {
        const errorMessage = getErrorValueResponse(error);
        setMessageErrorVolantes(errorMessage);
        setShowModalErrorVolantes(true);
      },
    }
  );

  useEffect(() => {
    getSenasaDams.execute({ filters });
    // eslint-disable-next-line
  }, []);

  const [filters, updateFilters] = useState(INITIAL_FILTERS);

  const auxiliaryArray = generateSequentialArray(ITEMS_PER_PAGE);

  const [showSenasaModal, updateShowSenasaModal] = useState(false);
  const [selectedDam, updateSelectedDam] = useState(0);
  const [showCommentModal, updateShowCommentModal] = useState(false);
  const [damComment, updateDamComment] = useState(false);

  const toggleCommentModal = () => {
    updateShowCommentModal(!showCommentModal);
  };

  const [messageErrorVolantes, setMessageErrorVolantes] = useState("");
  const [showModalErrorVolantes, setShowModalErrorVolantes] = useState(false);

  return (
    <div>
      {messageErrorVolantes && showModalErrorVolantes && (
        <ConfirmationModal
          message={messageErrorVolantes}
          onAccept={() => {
            setShowModalErrorVolantes(false);
          }}
          title={`Atención`}
          toggleModal={() => {
            setShowModalErrorVolantes(false);
          }}
        />
      )}
      <Title>DAM Afectadas</Title>
      <SenasaDamsFilter
        onQuery={(filters) => {
          getSenasaDams.execute({ filters });
          updateFilters(filters);
        }}
      />
      {getSenasaDams.isLoading &&
        auxiliaryArray.map((_, index) => {
          return <FakeRow key={index} />;
        })}

      {getSenasaDams.isSuccess &&
        getSenasaDams.data.fetchedDams.length === 0 && (
          <StandaloneMessage>No se encontraron datos.</StandaloneMessage>
        )}
      {getSenasaDams.isError && (
        <div>
          Algo salió mal
          <button
            type="button"
            onClick={() => {
              getSenasaDams.retry();
            }}
          >
            Reintentar
          </button>
        </div>
      )}

      <Authorization actions={["dam.get_dam_senasa_agent"]}>
        {getSenasaDams.isSuccess && getSenasaDams.data.fetchedDams.length > 0 && (
          <Container gridTemplateColumns="repeat(4, 1fr)">
            <TableHeader className="item-container">
              <div className="left-align">{TITLES.dam}</div>
              <div>{TITLES.status}</div>
              <div className="center">{TITLES.date}</div>
              <div>{TITLES.volantes}</div>
            </TableHeader>
            <TableBody>
              {getSenasaDams.data.fetchedDams.map((dam) => {
                const id = dam.id;
                const prefixDamId = id.slice(0, id.lastIndexOf("-") + 1);
                const suffixDamId = id.slice(id.lastIndexOf("-") + 1);

                const defineClickable = (damNum) => {
                  if (dam.status.label === "Rechazado") {
                    updateSelectedDam(damNum);
                    updateDamComment(dam.senasaComment);
                    updateShowCommentModal(true);
                  }
                };
                const className = `item-container ${
                  dam.status.label === "Rechazado" ? "clickable" : ""
                }`;
                return (
                  <Row
                    key={dam.id}
                    className={className}
                    onClick={() => defineClickable(dam.id)}
                  >
                    <div className="attribute left-align">
                      <div className="titles">{TITLES.dam}</div>
                      <div className="left-align">
                        <div>
                          <Span>{prefixDamId}</Span>
                          {suffixDamId}
                        </div>
                        <div>{dam.consigneeName}</div>
                      </div>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.status}</div>
                      <Authorization actions={["dam.update_dam_senasa_agent"]}>
                        <div>
                          <StatusTag status={dam.status} />
                        </div>
                      </Authorization>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.date}</div>
                      <div>
                        {" "}
                        {dam.updated_at
                          ? dateToRegularFormat(dam.updated_at)
                          : "-"}
                      </div>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.volantes}</div>
                      <div>{`${dam.paidVolantesNum}/${dam.volantesNum} volantes`}</div>
                    </div>
                  </Row>
                );
              })}
            </TableBody>

            <div className="flex-paginator">
              <Paginator
                first={
                  (getSenasaDams.data.page - 1) * getSenasaDams.data.page_size
                }
                rows={getSenasaDams.data.page_size}
                totalRecords={getSenasaDams.data.count}
                onPageChange={(e) => {
                  const targetPage = e.first / getSenasaDams.data.page_size;
                  getSenasaDams.execute({
                    page: targetPage + 1,
                    filters,
                  });
                }}
              />
            </div>
          </Container>
        )}
      </Authorization>

      {showCommentModal && (
        <CommentModal
          toggleModal={toggleCommentModal}
          title="Documento rechazado"
          message={damComment}
          onAccept={() => {
            updateShowSenasaModal(true);
            toggleCommentModal();
          }}
        />
      )}
      {showSenasaModal && (
        <FileUploadSenasaModal
          damNum={selectedDam}
          hideModal={() => updateShowSenasaModal(false)}
          getSenasaAndElectronicMandate={getSenasaDams}
        />
      )}
    </div>
  );
};

export default SenasaDams;
